import { useState } from 'react';
import { AlertTriangle, ArchiveRestore, Info, Loader2, Trash2 } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { Modal } from '../../components/Modal/Modal';
import { useToast } from '../../components/Toast';
import { mailLoeschen, type LoeschErgebnis, type PlanungsMail } from '../../lib/planung';
import { useFeedbackStore } from '../../stores/feedbackStore';
import './Dialoge.css';

type Props = {
  mail: Pick<PlanungsMail, 'id' | 'name' | 'betreff' | 'status' | 'brevo_id' | 'brevo_status' | 'anfragen'>;
  offen: boolean;
  onClose: () => void;
  /** Nach dem Löschen (Listen sind dann schon neu angestoßen). */
  onGeloescht?: (r: LoeschErgebnis) => void;
};

/** Was mit der Brevo-Kampagne passiert — nach dem zuletzt gelesenen Stand (bis 15 Minuten alt). */
function brevoFall(m: Props['mail']): 'keine' | 'entwurf' | 'angehalten' | 'eingeplant' | 'versand' | 'unbekannt' {
  if (!m.brevo_id) return 'keine';
  switch (m.brevo_status) {
    case 'draft': return 'entwurf';
    case 'suspended': return 'angehalten';
    case 'queued': return 'eingeplant';
    case 'in_process': case 'sent': case 'archive': return 'versand';
    default: return 'unbekannt';
  }
}

/**
 * Rückfrage vor dem Löschen. Sagt in Klartext, was passiert: Die Mail geht
 * unter „Gelöscht" und lässt sich wiederherstellen; eine Brevo-Kampagne im
 * Entwurf oder angehalten wird mitgelöscht; eingeplante erst in Brevo anhalten.
 * Entschieden wird auf dem Server (brevo-draft „loeschen“).
 */
export function LoeschenDialog({ mail, offen, onClose, onGeloescht }: Props) {
  const toast = useToast();
  const qc = useQueryClient();
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const titel = mail.betreff?.trim() || mail.name || 'Ohne Betreff';
  const fall = brevoFall(mail);
  const gesperrt = fall === 'versand' || mail.status === 'sent' || mail.status === 'sending';

  const schliessen = () => {
    if (laeuft) return;
    setFehler(null);
    onClose();
  };

  async function loeschen() {
    setLaeuft(true);
    setFehler(null);
    try {
      const r = await mailLoeschen(mail.id);
      toast.success(r.brevo === 'geloescht'
        ? 'Gelöscht — auch die Kampagne in Brevo. Unter „Gelöscht“ lässt sie sich wiederherstellen.'
        : 'Gelöscht. Unter „Gelöscht“ lässt sie sich wiederherstellen.');
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['planung-liste'] }),
        qc.invalidateQueries({ queryKey: ['planung-geloescht'] }),
        qc.invalidateQueries({ queryKey: ['feedback-alle'] }),
      ]);
      void useFeedbackStore.getState().laden();
      setLaeuft(false);
      onGeloescht?.(r);
      onClose();
      // Erst nach dem Wegnavigieren: Die Werkstatt soll die gelöschte Mail nicht neu laden.
      qc.removeQueries({ queryKey: ['planung-mail', mail.id] });
    } catch (err) {
      const text = err instanceof Error ? err.message : String(err);
      setFehler(text);
      setLaeuft(false);
      // Der Server hat womöglich den echten Brevo-Stand gemerkt (z. B. eingeplant).
      void qc.invalidateQueries({ queryKey: ['planung-liste'] });
      void qc.invalidateQueries({ queryKey: ['planung-mail', mail.id] });
    }
  }

  return (
    <Modal isOpen={offen} onClose={schliessen} title="Mail löschen?" size="sm">
      <div className="gd-form">
        <p className="gd-betreff">{titel}</p>

        <ul className="gd-loeschen-folgen">
          <li>
            <Trash2 size={15} aria-hidden="true" />
            <span>Die Mail verschwindet aus der Übersicht, dem Kalender und den Feedback-Links{mail.anfragen.length > 0 ? ' der Ansprechpersonen' : ''}.</span>
          </li>
          <li>
            <ArchiveRestore size={15} aria-hidden="true" />
            <span>Sie steht danach unter <strong>„Gelöscht“</strong> und lässt sich von dort wiederherstellen — mit Inhalt, Versionen, Rückmeldungen und Terminen.</span>
          </li>
        </ul>

        {(fall === 'entwurf' || fall === 'angehalten') && (
          <p className="gd-hinweis gd-hinweis-zeile gd-hinweis--warn">
            <AlertTriangle size={15} aria-hidden="true" />
            <span>
              In Brevo {fall === 'entwurf' ? 'liegt sie als Entwurf' : 'ist sie angehalten'}: Die Brevo-Kampagne wird mitgelöscht.
              Nach dem Wiederherstellen muss sie in Brevo neu angelegt werden.
            </span>
          </p>
        )}
        {fall === 'eingeplant' && (
          <p className="gd-hinweis gd-hinweis-zeile gd-hinweis--warn">
            <AlertTriangle size={15} aria-hidden="true" />
            <span>
              In Brevo ist sie <strong>eingeplant</strong> und würde verschickt. Halte sie zuerst in Brevo an
              (Kampagne → „Anhalten“) und lösche sie dann hier — sonst lehnt das Mailtool ab.
            </span>
          </p>
        )}
        {fall === 'unbekannt' && (
          <p className="gd-hinweis gd-hinweis-zeile">
            <Info size={15} aria-hidden="true" />
            <span>Es gibt eine Brevo-Kampagne. Ist sie dort Entwurf oder angehalten, wird sie mitgelöscht; ist sie eingeplant, erst in Brevo anhalten.</span>
          </p>
        )}
        {gesperrt && (
          <p className="gd-hinweis gd-hinweis-zeile gd-hinweis--warn">
            <AlertTriangle size={15} aria-hidden="true" />
            <span>Brevo verschickt diese Mail gerade oder hat sie schon verschickt — versendete Mails lassen sich nicht löschen.</span>
          </p>
        )}

        {fehler && <p className="gd-fehler" role="alert">{fehler}</p>}

        <div className="form-actions">
          <button type="button" className="btn btn-ghost" onClick={schliessen} disabled={laeuft}>Abbrechen</button>
          <button type="button" className="btn btn-danger" onClick={() => void loeschen()} disabled={laeuft || gesperrt} autoFocus={!gesperrt}>
            {laeuft
              ? <><Loader2 size={16} className="spin" aria-hidden="true" /> Löscht …</>
              : <><Trash2 size={16} aria-hidden="true" /> {fall === 'entwurf' || fall === 'angehalten' ? 'Hier und in Brevo löschen' : 'Löschen'}</>}
          </button>
        </div>
      </div>
    </Modal>
  );
}
